import React, { useState } from 'react';
import { carteraApi } from './api';
import { money } from './helpers';

const CANALES = [
  ['transferencia', 'Transferencia'],
  ['spei', 'SPEI'],
  ['domiciliacion', 'Domiciliación'],
  ['efectivo', 'Efectivo'],
];

const hoy = () => new Date().toISOString().slice(0, 10);

// Parseo tolerante: acepta "26,234", "$26,234.50", "26 234" → número. El <input>
// era type=number y truncaba la coma del paste ("26,234" → 26.23); por eso ahora
// es texto y se limpia aquí.
const parseMonto = (s) => {
  const n = parseFloat(String(s ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// Mes-año corto para "Termina: feb-2027 → ene-2027" en la vista previa del abono.
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const mesAno = (iso) => {
  if (!iso) return '—';
  const [y, m] = String(iso).slice(0, 10).split('-');
  const idx = Number(m) - 1;
  return MESES[idx] != null ? `${MESES[idx]}-${y}` : String(iso).slice(0, 10);
};

export const RegistrarPagoModal = ({ credito, onClose, onDone, proximaMensualidad = 0, alCorriente = 0, completarCuota = 0, maxPagar = 0 }) => {
  // modo: pago (normal, comportamiento original) | capital (abono a capital) |
  // liquidar (liquidación anticipada AF/CS o AP con desglose del backend).
  const [modo, setModo] = useState('pago');
  const [monto, setMonto] = useState('');
  const [condonar, setCondonar] = useState(false); // perdonar la mora: el pago va directo a cuotas
  const [canal, setCanal] = useState('transferencia');
  const [fecha, setFecha] = useState(hoy());
  // Sobrante del pago normal: 'cuotas' = adelanto (default) o 'capital' = tras
  // cubrir lo pendiente, el remanente baja la deuda y la tabla se recalcula
  // (mismo motor del chip "Abono a capital", vía pago mixto).
  const [aplicarA, setAplicarA] = useState('cuotas');
  const [notaResultado, setNotaResultado] = useState(null);
  const [notas, setNotas] = useState('');
  const [fileName, setFileName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);          // éxito de pago normal
  const [preview, setPreview] = useState(null);        // respuesta de POST /abono-capital/preview
  const [previewLoading, setPreviewLoading] = useState(false);
  const [resCap, setResCap] = useState(null);          // éxito del abono {pagoCapital, pagoDeudas}
  const [liq, setLiq] = useState(null);                // desglose de GET /liquidacion
  const [liqLoading, setLiqLoading] = useState(false);
  const [resLiq, setResLiq] = useState(null);          // éxito de POST /liquidar (pago)
  const [moraLiq, setMoraLiq] = useState('cobrar');    // liquidación: cobrar | parte | condonar
  const [moraCobrar, setMoraCobrar] = useState('');    // liquidación 'parte': mora que SÍ se cobra
  const [liqParams, setLiqParams] = useState(null);    // params del último desglose (se confirman tal cual)
  const [saldoLiq, setSaldoLiq] = useState(null);      // saldo del crédito que devolvió /liquidar (0)

  const c = credito || {};
  const esPuro = c.tipo === 'arrendamiento_puro';

  const montoNum = parseMonto(monto);
  const mora = Number(c.mora_acumulada || 0);
  // Con mora condonada, la mora sale de los montos sugeridos y del tope.
  const moraEfectiva = condonar ? 0 : mora;
  // Liquidación: la mora del back (cobrada + condonada) si ya hay desglose; si no, la del crédito.
  const moraLiqTotal = liq ? Number(liq.mora || 0) + Number(liq.mora_condonada || 0) : mora;
  const maxAjustado = maxPagar > 0 ? Math.max(0, maxPagar - (condonar ? mora : 0)) : 0;
  const alCorrienteAjustado = Math.max(0, alCorriente - (condonar ? mora : 0));
  const topado = maxAjustado > 0 && montoNum > maxAjustado; // excede lo que resta del contrato
  const efectivo = topado ? maxAjustado : montoNum;         // lo que de verdad se cobrará

  // Reparto del pago mixto que reporta el preview (deudas primero, remanente a
  // capital). Tolerante a nombres alternos por si el back varía la clave.
  const rep = preview?.reparto || null;
  const repMora = Number(rep?.mora || 0);
  const repServ = Number(rep?.servicios ?? rep?.servicios_vencidos ?? 0);
  const repVenc = Number(rep?.vencidas ?? rep?.cuotas_vencidas ?? 0);
  // Cuotas futuras con abono parcial: se cubren completas para dejar limpia la
  // base de regeneración, pero NO son vencidas (etiqueta aparte para no mentir).
  const repParc = Number(rep?.cuotas_parciales ?? 0);
  const repCap = Number(rep?.capital ?? rep?.a_capital ?? 0);
  const repDeuda = Number(rep?.deuda ?? (repMora + repServ + repVenc + repParc));
  const repPartes = [];
  if (repMora > 0) repPartes.push(`${money(repMora)} a mora`);
  if (repServ > 0) repPartes.push(`${money(repServ)} a servicios vencidos`);
  if (repVenc > 0) repPartes.push(`${money(repVenc)} a cuotas vencidas`);
  if (repParc > 0) repPartes.push(`${money(repParc)} a completar la cuota en curso`);
  if (repDeuda > 0) repPartes.push(`${money(repCap)} a capital`);

  const hayResultado = !!(result || resCap || resLiq);
  const titulo = hayResultado
    ? (resLiq ? 'Crédito liquidado' : resCap ? 'Abono a capital aplicado' : 'Pago registrado')
    : modo === 'capital' ? 'Abono a capital'
      : modo === 'liquidar' ? 'Liquidación anticipada'
        : 'Registrar pago';

  async function submit() {
    const m = Math.min(parseMonto(monto), maxAjustado > 0 ? maxAjustado : Infinity);
    if (!m || m <= 0) { setError('Ingresa un monto mayor a 0.'); return; }
    setSubmitting(true);
    setError(null);
    setNotaResultado(null);
    try {
      // Sobrante → capital: se rutea por el motor de abono (pago mixto: cubre
      // deudas primero, el remanente recalcula la tabla). Si el preview dice que
      // no habrá remanente, se degrada a pago normal con aviso.
      if (!esPuro && aplicarA === 'capital') {
        const prev = await carteraApi.abonoCapitalPreview(c.id, { monto: m, fecha });
        const p = prev?.data?.data ?? prev?.data ?? {};
        if (p.valido) {
          const res = await carteraApi.abonoCapital(c.id, { monto: m, canal, fecha, notas: notas || null });
          const data = res?.data?.data ?? res?.data ?? {};
          setResCap({ pagoCapital: data.pago_capital ?? data.pago ?? data, pagoDeudas: data.pago_deudas ?? null });
          return;
        }
        if (p.codigo !== 'no_alcanza') {
          // Bloqueado (interés-solo, tabla irregular, tope…): que el admin decida.
          setError(p.error || 'Este contrato no permite abono a capital.');
          return;
        }
        setNotaResultado('El pago no tuvo sobrante: se aplicó todo a lo pendiente, sin abono a capital.');
      }
      const res = await carteraApi.registrarPago(c.id, {
        monto: m,
        canal,
        fecha,
        aplicar_a: 'cuotas', // el sobrante de un pago normal siempre es adelanto
        condonar_mora: condonar,
        notas: notas || null,
        estatus: 'aplicado',
        // comprobante_url: pendiente de endpoint de almacenamiento de archivos.
      });
      setResult(res?.data?.data?.pago || null);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo registrar el pago. Verifica los datos.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Abono a capital ─────────────────────────────────────────────────────────
  function abrirCapital() {
    setModo('capital');
    setError(null);
    setPreview(null);
  }

  function volverAPago() {
    setModo('pago');
    setError(null);
    setPreview(null);
    setLiq(null);
  }

  async function verEfecto() {
    const m = parseMonto(monto);
    if (!m || m <= 0) { setError('Ingresa un monto mayor a 0.'); return; }
    setPreviewLoading(true);
    setError(null);
    setPreview(null);
    try {
      const res = await carteraApi.abonoCapitalPreview(c.id, { monto: m, fecha });
      setPreview(res?.data?.data ?? res?.data ?? null);
    } catch (e) {
      // Exclusiones del back (interés-solo, tabla irregular, en revisión…) llegan
      // como 422 con message, o como {valido:false, error} dentro del preview.
      setError(e?.response?.data?.message || 'No se pudo calcular la vista previa del abono.');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function confirmarAbono() {
    const m = parseMonto(monto);
    if (!m || m <= 0) { setError('Ingresa un monto mayor a 0.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await carteraApi.abonoCapital(c.id, { monto: m, canal, fecha, notas: notas || null });
      const data = res?.data?.data ?? res?.data ?? {};
      // Pago mixto: puede venir un Pago A (deudas) ligado además del abono.
      setResCap({ pagoCapital: data.pago_capital ?? data.pago ?? data, pagoDeudas: data.pago_deudas ?? null });
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo aplicar el abono a capital.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Liquidación anticipada (AF/CS y AP) ─────────────────────────────────────
  // El desglose sale del back. Mora: 'cobrar' (toda), 'parte' (se cobra lo que se
  // capture y el resto se perdona) o 'condonar' (toda). Se confirma con los MISMOS
  // params del último desglose (liqParams): nunca con un monto sin recalcular.
  async function cargarLiquidacion(params) {
    setError(null);
    setLiq(null);
    setLiqParams(null);
    setLiqLoading(true);
    try {
      const res = await carteraApi.liquidacionPreview(c.id, undefined, params);
      setLiq(res?.data?.data ?? res?.data ?? null);
      setLiqParams(params);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo calcular el total de liquidación.');
    } finally {
      setLiqLoading(false);
    }
  }

  function abrirLiquidacion() {
    setModo('liquidar');
    setMoraLiq('cobrar');
    setMoraCobrar('');
    cargarLiquidacion({});
  }

  function cambiarMoraLiq(m) {
    if (m === moraLiq) return;
    setMoraLiq(m);
    if (m === 'parte') {
      // Hasta capturar el monto y recalcular, el desglose anterior ya no aplica.
      setError(null);
      setLiq(null);
      setLiqParams(null);
      return;
    }
    cargarLiquidacion(m === 'condonar' ? { condonar_mora: 1 } : {});
  }

  function recalcularParte() {
    if (String(moraCobrar).trim() === '') { setError('Captura cuánto de la mora se cobra.'); return; }
    cargarLiquidacion({ mora_a_cobrar: parseMonto(moraCobrar) });
  }

  async function confirmarLiquidacion() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await carteraApi.liquidar(c.id, { canal, fecha, notas: notas || null, ...(liqParams || {}) });
      const data = res?.data?.data ?? res?.data ?? {};
      setResLiq(data.pago ?? data);
      if (data.credito?.saldo_insoluto != null) setSaldoLiq(data.credito.saldo_insoluto);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo liquidar el crédito.');
    } finally {
      setSubmitting(false);
    }
  }

  function cerrarOk() {
    if (onDone) onDone();
    onClose();
  }

  return (
    <div className="cart-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="cart-modal">
        <div className="cm-head">
          <h3>{titulo}</h3>
          <button className="cm-close" onClick={() => (hayResultado ? cerrarOk() : onClose())}><i className="pi pi-times" /></button>
        </div>
        <div className="cm-credit">
          <b>{c.cliente || '—'}</b> · {c.clave_contrato} · saldo <b>{money(saldoLiq ?? c.saldo_insoluto)}</b>
        </div>

        {result ? (
          <>
            <div className="cm-result">
              <div className="cr-folio">Folio {result.folio}</div>
              <div className="cr-row"><span>Monto pagado</span><b>{money(result.monto_pagado)}</b></div>
              {Number(result.aplicacion_cargos) > 0 && (
                <div className="cr-row"><span>A servicios (seguro · placas · GPS)</span><b>{money(result.aplicacion_cargos)}</b></div>
              )}
              <div className="cr-row"><span>A capital</span><b>{money(result.aplicacion_capital)}</b></div>
              <div className="cr-row"><span>A interés (+IVA)</span><b>{money(Number(result.aplicacion_interes) + Number(result.aplicacion_iva_interes))}</b></div>
              <div className="cr-row"><span>A mora</span><b>{money(result.aplicacion_mora)}</b></div>
              {Number(result.meta?.mora_condonada) > 0 && (
                <div className="cr-row"><span>Mora condonada</span><b style={{ color: '#a4640a' }}>{money(result.meta.mora_condonada)}</b></div>
              )}
              {Number(result.excedente) > 0 && <div className="cr-row"><span>Excedente</span><b>{money(result.excedente)}</b></div>}
            </div>
            {notaResultado && <div className="cm-note" style={{ marginTop: 8 }}>{notaResultado}</div>}
            <div className="cm-actions">
              <button className="btn btn-green btn-sm" onClick={cerrarOk}><i className="pi pi-check" />Listo</button>
            </div>
          </>
        ) : resCap ? (
          <>
            {/* Éxito del abono: si hubo pago mixto se muestran ambos folios ligados. */}
            <div className="cm-result">
              <div className="cr-folio">
                Folio {resCap.pagoCapital?.folio || '—'}
                {resCap.pagoDeudas ? ` · deudas ${resCap.pagoDeudas.folio}` : ''}
              </div>
              {resCap.pagoDeudas && (
                <div className="cr-row"><span>Aplicado a deudas (mora · vencidas)</span><b>{money(resCap.pagoDeudas.monto_pagado)}</b></div>
              )}
              <div className="cr-row"><span>Abono a capital</span><b>{money(resCap.pagoCapital?.aplicacion_capital ?? resCap.pagoCapital?.monto_pagado)}</b></div>
              {Number(resCap.pagoCapital?.meta?.comision) > 0 && (
                <div className="cr-row"><span>Comisión (cobrada aparte)</span><b>{money(resCap.pagoCapital.meta.comision)}</b></div>
              )}
              {resCap.pagoCapital?.meta?.saldo_despues != null && (
                <div className="cr-row"><span>Nuevo saldo insoluto</span><b>{money(resCap.pagoCapital.meta.saldo_despues)}</b></div>
              )}
              {Number(resCap.pagoCapital?.meta?.interes_ahorrado) > 0 && (
                <div className="cr-row"><span>Interés ahorrado</span><b style={{ color: '#06794b' }}>{money(resCap.pagoCapital.meta.interes_ahorrado)}</b></div>
              )}
              <div className="cr-row"><span>Tabla de amortización</span><b>recalculada (misma cuota, menos plazo)</b></div>
            </div>
            <div className="cm-actions">
              <button className="btn btn-green btn-sm" onClick={cerrarOk}><i className="pi pi-check" />Listo</button>
            </div>
          </>
        ) : resLiq ? (
          <>
            <div className="cm-result">
              <div className="cr-folio">Folio {resLiq.folio || '—'}</div>
              <div className="cr-row"><span>Total pagado</span><b>{money(resLiq.monto_pagado)}</b></div>
              {Number(resLiq.meta?.mora_condonada) > 0 && (
                <div className="cr-row"><span>Mora condonada</span><b style={{ color: '#a4640a' }}>{money(resLiq.meta.mora_condonada)}</b></div>
              )}
              {Number(resLiq.meta?.liquidacion?.comision) > 0 && (
                <div className="cr-row"><span>Comisión por liquidación</span><b>{money(resLiq.meta.liquidacion.comision)}</b></div>
              )}
              <div className="cr-row"><span>Estatus del crédito</span><b>Liquidado</b></div>
            </div>
            <div className="cm-actions">
              <button className="btn btn-green btn-sm" onClick={cerrarOk}><i className="pi pi-check" />Listo</button>
            </div>
          </>
        ) : modo === 'capital' ? (
          <>
            {error && <div className="cm-error">{error}</div>}

            <div className="cm-field">
              <label>Monto del abono</label>
              <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                value={monto}
                onChange={(e) => { setMonto(e.target.value); if (preview) setPreview(null); }}
                autoFocus />
              {montoNum > 0 && <div className="cm-note">= {money(montoNum)}</div>}
              {alCorriente > 0 && (
                <div className="cm-note" style={{ color: '#a4640a', fontWeight: 600 }}>
                  Este crédito debe {money(alCorriente)} (vencido + mora): tu abono lo cubre primero y solo el resto baja a capital.
                </div>
              )}
            </div>

            <div className="cm-row">
              <div className="cm-field">
                <label>Canal</label>
                <select className="cm-input" value={canal} onChange={(e) => setCanal(e.target.value)}>
                  {CANALES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="cm-field">
                <label>Fecha</label>
                <input className="cm-input" type="date" value={fecha}
                  onChange={(e) => { setFecha(e.target.value); if (preview) setPreview(null); }} />
              </div>
            </div>

            <div className="cm-field">
              <label>Notas (opcional)</label>
              <textarea className="cm-input" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Referencia bancaria, observaciones…" />
            </div>

            {/* Bloqueo del back (interés-solo, tabla irregular, no alcanza, monto
                fuera de tope…): se muestra tal cual su mensaje. */}
            {preview && preview.valido === false && (
              <div className="cm-error">
                {preview.error || 'El abono a capital no se puede aplicar a este crédito.'}
              </div>
            )}

            {/* Vista previa (sin escribir): reparto del pago mixto + efecto en la tabla. */}
            {preview && preview.valido !== false && (
              <div className="cm-result" style={{ background: '#F0F9FF', marginBottom: 10 }}>
                {repDeuda > 0 && (
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#a4640a', marginBottom: 6 }}>
                    De tus {money(montoNum)}: {repPartes.join(' · ')}
                  </div>
                )}
                <div className="cr-row"><span>Saldo insoluto</span><b>{money(preview.saldo_antes)} → {money(preview.saldo_despues)}</b></div>
                <div className="cr-row">
                  <span>Termina</span>
                  <b>{mesAno(preview.fecha_fin_antes)} → {mesAno(preview.fecha_fin_despues)}</b>
                </div>
                <div className="cr-row">
                  <span>Cuotas restantes</span>
                  <b>{preview.cuotas_antes} → {preview.cuotas_despues} (última {money(preview.ultima_cuota_nueva)})</b>
                </div>
                <div className="cr-row"><span>Interés que se ahorra</span><b style={{ color: '#06794b' }}>{money(preview.ahorro)}</b></div>
                {Number(preview.comision) > 0 && (
                  <div className="cr-row"><span>Comisión (se cobra aparte)</span><b>{money(preview.comision)}</b></div>
                )}
                <div className="cm-note">La cuota mensual no cambia: el contrato termina antes.</div>
              </div>
            )}

            <div className="cm-actions">
              <button className="btn btn-ghost btn-sm" onClick={volverAPago} disabled={submitting}>Volver</button>
              {preview && preview.valido !== false ? (
                <button className="btn btn-green btn-sm" onClick={confirmarAbono} disabled={submitting}>
                  <i className={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'} />{submitting ? 'Aplicando…' : 'Confirmar abono'}
                </button>
              ) : (
                <button className="btn btn-green btn-sm" onClick={verEfecto} disabled={previewLoading || montoNum <= 0}>
                  <i className={previewLoading ? 'pi pi-spinner pi-spin' : 'pi pi-eye'} />{previewLoading ? 'Calculando…' : 'Ver efecto'}
                </button>
              )}
            </div>
          </>
        ) : modo === 'liquidar' ? (
          <>
            {error && <div className="cm-error">{error}</div>}
            {liqLoading && <div className="cm-note" style={{ marginBottom: 12 }}>Calculando el total de liquidación…</div>}

            {/* Desglose del back: saldo (ya incluye residual si el contrato tiene)
                + mora + servicios vencidos + comisión. En puro el saldo es el capital
                de la amortización referencial; las rentas vencidas se suman completas
                y lo abonado a rentas que aún no vencen se descuenta. */}
            {liq && (
              <div className="cm-result" style={{ background: '#F0F9FF', marginBottom: 14 }}>
                <div className="cr-row">
                  <span>
                    {esPuro
                      ? `Capital pendiente${Number(liq.residual) > 0 ? ` (incluye residual ${money(liq.residual)})` : ''}`
                      : 'Saldo insoluto'}
                  </span>
                  <b>{money(liq.saldo_insoluto)}</b>
                </div>
                {Number(liq.rentas_vencidas) > 0 && (
                  <div className="cr-row"><span>Rentas vencidas</span><b>{money(liq.rentas_vencidas)}</b></div>
                )}
                {Number(liq.abonos_previos) > 0 && (
                  <div className="cr-row"><span>Abonos ya recibidos</span><b>−{money(liq.abonos_previos)}</b></div>
                )}
                {Number(liq.mora) > 0 && (
                  <div className="cr-row"><span>{Number(liq.mora_condonada) > 0 ? 'Mora que se cobra' : 'Mora acumulada'}</span><b>{money(liq.mora)}</b></div>
                )}
                {Number(liq.mora_condonada) > 0 && (
                  <div className="cr-row"><span>Mora condonada (no se cobra)</span><b style={{ color: '#a4640a' }}>{money(liq.mora_condonada)}</b></div>
                )}
                {Number(liq.servicios_vencidos) > 0 && (
                  <div className="cr-row"><span>Servicios vencidos</span><b>{money(liq.servicios_vencidos)}</b></div>
                )}
                {Number(liq.comision) > 0 && (
                  <div className="cr-row"><span>Comisión por liquidación</span><b>{money(liq.comision)}</b></div>
                )}
                <div className="cr-row" style={{ fontWeight: 800, borderTop: '1px solid rgba(12,32,54,.12)', marginTop: 4, paddingTop: 8 }}>
                  <span>Total para liquidar</span><b>{money(liq.total)}</b>
                </div>
                <div className="cm-note">
                  {esPuro && liq.rentas_por_cerrar
                    ? `Al confirmar, se cierran ${liq.rentas_por_cerrar} rentas (desde la #${liq.desde_renta}) y el contrato queda liquidado.`
                    : 'Al confirmar, las cuotas pendientes se cierran (sin intereses futuros) y el crédito queda liquidado.'}
                  {' '}No se puede deshacer.
                </div>
              </div>
            )}

            {moraLiqTotal > 0 && (
              <div className="cm-field">
                <label>Mora acumulada · {money(moraLiqTotal)}</label>
                <div className="cm-seg">
                  <button className={moraLiq === 'cobrar' ? 'on' : ''} onClick={() => cambiarMoraLiq('cobrar')} disabled={liqLoading || submitting}>Cobrar mora</button>
                  <button className={moraLiq === 'parte' ? 'on' : ''} onClick={() => cambiarMoraLiq('parte')} disabled={liqLoading || submitting}>Cobrar una parte</button>
                  <button className={moraLiq === 'condonar' ? 'on' : ''} onClick={() => cambiarMoraLiq('condonar')} disabled={liqLoading || submitting}>Condonar (perdonar)</button>
                </div>
                {moraLiq === 'parte' && (
                  <>
                    <input className="cm-input" type="text" inputMode="decimal" placeholder="Mora a cobrar" style={{ marginTop: 8 }}
                      value={moraCobrar}
                      onChange={(e) => { setMoraCobrar(e.target.value); if (liqParams) { setLiq(null); setLiqParams(null); } }}
                      onKeyDown={(e) => { if (e.key === 'Enter') recalcularParte(); }} />
                    <div className="cm-quick">
                      <button type="button" className="cm-chip" onClick={recalcularParte} disabled={liqLoading || submitting}>Recalcular</button>
                    </div>
                    {!liq && !liqLoading && (
                      <div className="cm-note">Captura cuánto de la mora se cobra (el resto se perdona) y dale Recalcular.</div>
                    )}
                  </>
                )}
                {Number(liq?.mora_condonada) > 0 && (
                  <div className="cm-note" style={{ color: '#a4640a', fontWeight: 600 }}>
                    Se perdonan {money(liq.mora_condonada)} de mora: no entran en el total y quedan registrados como condonados.
                  </div>
                )}
              </div>
            )}

            <div className="cm-row">
              <div className="cm-field">
                <label>Canal</label>
                <select className="cm-input" value={canal} onChange={(e) => setCanal(e.target.value)}>
                  {CANALES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="cm-field">
                <label>Fecha</label>
                <input className="cm-input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
              </div>
            </div>

            <div className="cm-field">
              <label>Notas (opcional)</label>
              <textarea className="cm-input" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Referencia bancaria, observaciones…" />
            </div>

            <div className="cm-actions">
              <button className="btn btn-ghost btn-sm" onClick={volverAPago} disabled={submitting}>Volver</button>
              <button className="btn btn-green btn-sm" onClick={confirmarLiquidacion} disabled={submitting || liqLoading || !liq}>
                <i className={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'} />{submitting ? 'Liquidando…' : 'Confirmar liquidación'}
              </button>
            </div>
          </>
        ) : (
          <>
            {error && <div className="cm-error">{error}</div>}

            <div className="cm-field">
              <label>Monto</label>
              <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                value={monto} onChange={(e) => setMonto(e.target.value)} autoFocus />
              {montoNum > 0 && (
                <div className="cm-note" style={topado ? { color: '#a4640a', fontWeight: 600 } : undefined}>
                  = {money(efectivo)}
                  {topado
                    ? ` · topado al máximo del contrato (${money(maxAjustado)})`
                    : (moraEfectiva > 0 ? ` · primero cubre la mora de ${money(moraEfectiva)}` : '')}
                </div>
              )}
              {(proximaMensualidad > 0 || alCorriente > 0 || maxPagar > 0 || !esPuro) && (
                <div className="cm-quick">
                  {completarCuota > 0.005 && (
                    <button type="button" className="cm-chip cm-chip-amber" onClick={() => setMonto(completarCuota.toFixed(2))}>
                      Completar cuota {money(completarCuota)}
                    </button>
                  )}
                  {proximaMensualidad > 0 && (
                    <button type="button" className="cm-chip" onClick={() => setMonto(proximaMensualidad.toFixed(2))}>
                      Mensualidad {money(proximaMensualidad)}
                    </button>
                  )}
                  {alCorrienteAjustado > 0 && Math.abs(alCorrienteAjustado - proximaMensualidad) > 1 && (
                    <button type="button" className="cm-chip cm-chip-amber" onClick={() => setMonto(alCorrienteAjustado.toFixed(2))}>
                      Ponerse al corriente {money(alCorrienteAjustado)}
                    </button>
                  )}
                  {/* Liquidar anticipado (desglose del back) se ofrece SIEMPRE que el
                      crédito siga vivo. AF/CS: saldo + mora + comisión — con una sola
                      cuota pendiente el back rechaza el abono a capital y manda justo
                      a este chip. Puro (oct-2026): capital de la amortización
                      referencial, ya no las rentas restantes completas. */}
                  {maxAjustado > 0 && (
                    <button type="button" className="cm-chip cm-chip-green" onClick={abrirLiquidacion}>
                      Liquidar anticipado
                    </button>
                  )}
                  {/* Abono a capital: solo AF/CS (en puro no hay capital). Nunca se
                      bloquea por deudas: el preview reparte deuda→capital (pago mixto). */}
                  {!esPuro && (
                    <button type="button" className="cm-chip" onClick={abrirCapital}>
                      Abono a capital
                    </button>
                  )}
                </div>
              )}
            </div>

            {mora > 0 && (
              <div className="cm-field">
                <label>Mora acumulada · {money(mora)}</label>
                <div className="cm-seg">
                  <button className={!condonar ? 'on' : ''} onClick={() => setCondonar(false)}>Cobrar mora</button>
                  <button className={condonar ? 'on' : ''} onClick={() => setCondonar(true)}>Condonar (perdonar)</button>
                </div>
                {condonar && (
                  <div className="cm-note" style={{ color: '#a4640a', fontWeight: 600 }}>
                    Se perdona la mora de {money(mora)}: todo el pago se aplica a las cuotas.
                  </div>
                )}
              </div>
            )}

            <div className="cm-row">
              <div className="cm-field">
                <label>Canal</label>
                <select className="cm-input" value={canal} onChange={(e) => setCanal(e.target.value)}>
                  {CANALES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="cm-field">
                <label>Fecha</label>
                <input className="cm-input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
              </div>
            </div>

            {/* Sobrante del pago normal: adelanto (default) o abono a capital REAL
                — si elige capital, el submit rutea por el motor de abono (pago
                mixto + recálculo), no por el aplicar_a=capital viejo del motor de
                pagos (que solo dejaba el sobrante flotando). En puro no hay
                capital: siempre prepaga rentas. */}
            <div className="cm-field">
              <label>Aplicar sobrante a</label>
              {esPuro ? (
                <>
                  <div className="cm-locked"><i className="pi pi-lock" />Siguientes rentas</div>
                  <div className="cm-note">En puro no hay capital: el sobrante siempre prepaga las rentas.</div>
                </>
              ) : (
                <>
                  <div className="cm-seg">
                    <button className={aplicarA === 'cuotas' ? 'on' : ''} onClick={() => setAplicarA('cuotas')}>Siguientes cuotas</button>
                    <button className={aplicarA === 'capital' ? 'on' : ''} onClick={() => setAplicarA('capital')}>Abono a capital</button>
                  </div>
                  {aplicarA === 'capital' && (
                    <div className="cm-note">Se cubre primero lo pendiente; el sobrante baja la deuda y la tabla se recalcula (terminas antes).</div>
                  )}
                </>
              )}
            </div>

            <div className="cm-field">
              <label>Comprobante (opcional)</label>
              <label className="cm-drop">
                <i className="pi pi-cloud-upload" />
                {fileName || 'Sube la foto del comprobante'}
                <input type="file" accept="image/*,application/pdf" onChange={(e) => setFileName(e.target.files?.[0]?.name || '')} />
              </label>
              <div className="cm-note">El archivo se guardará cuando se conecte el almacenamiento (pendiente).</div>
            </div>

            <div className="cm-field">
              <label>Notas (opcional)</label>
              <textarea className="cm-input" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Referencia bancaria, observaciones…" />
            </div>

            <div className="cm-actions">
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>Cancelar</button>
              <button className="btn btn-green btn-sm" onClick={submit} disabled={submitting}>
                <i className={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'} />{submitting ? 'Registrando…' : 'Registrar pago'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RegistrarPagoModal;
